import React, { useEffect } from 'react';
import { Form, Input, Select } from 'antd';
import styled from 'styled-components';

import Box from 'components/Box';
import Button from 'components/Button';
import SwitchV2 from 'components/SwitchV2';
import Text from 'components/Text';
import settingApis from 'features/settingService/services/apis';
import useGetFeeCreditCard from 'features/settingService/services/hooks/useGetFeeCreditCard';
import { getMessageError } from 'services/response';
import toast from 'utils/toast';

const FeeTypeOptions = [
  { value: 'MONEY', label: 'Dollar' },
  { value: 'PERCENT', label: 'Percent' },
];

interface FeeFormValues {
  id: string;
  enable: boolean;
  feeType: string;
  tipFeeType: string;
  feeValue: number;
  tipFeeValue: number;
}

const Fee = () => {
  const [form] = Form.useForm<FeeFormValues>();
  const [data, mutate] = useGetFeeCreditCard();

  useEffect(() => {
    if (!data) return;
    form.setFieldsValue({
      id: data.id,
      enable: data.enable,
      feeType: data.feeType,
      tipFeeType: data.tipFeeType,
      feeValue: data.feeValue,
      tipFeeValue: data.tipFeeValue,
    });
  }, [data]);

  const handleReset = () => {
    form.setFieldsValue({
      enable: data?.enable,
      feeType: data?.feeType,
      tipFeeType: data?.tipFeeType,
      feeValue: data?.feeValue,
      tipFeeValue: data?.tipFeeValue,
    });
  };

  const handleSubmit = async (values: FeeFormValues) => {
    if (!data) return;

    const body = {
      id: data?.id,
      enable: values.enable,
      feeType: values.feeType,
      tipFeeType: values.tipFeeType,
      feeValue: values.enable ? values.feeValue : 0,
      tipFeeValue: values.enable ? values.tipFeeValue : 0,
    };

    try {
      const res = await settingApis.updateFeeCreditCard(data?.shopId, body);
      if (res?.data?.data) {
        toast.success('Update fee credit card successfully');
        mutate();
      }
    } catch (error) {
      toast.error(getMessageError(error) || 'Update fee credit card failed');
    }
  };

  return (
    <Container>
      <Form form={form} layout='vertical' onFinish={handleSubmit}>
        <Form.Item name='id' hidden>
          <Input />
        </Form.Item>
        <Box display='flex' gap='2'>
          <Form.Item name='enable' valuePropName='checked' style={{ marginBottom: '10px' }}>
            <SwitchV2 title='Have Fee' />
          </Form.Item>
          <Text variant='H5'>Have Fee</Text>
        </Box>
        <Form.Item shouldUpdate>
          {({ getFieldValue }) =>
            getFieldValue('enable') && (
              <>
                <Content>
                  <Form.Item name='feeType' label='Type Fee Credit Card'>
                    <Select placeholder='Select' options={FeeTypeOptions} />
                  </Form.Item>
                  <Form.Item name='tipFeeType' label='Type Fee Tip Credit Card'>
                    <Select placeholder='Select' options={FeeTypeOptions} />
                  </Form.Item>
                  <Form.Item
                    name='feeValue'
                    label='Fee Credit Card'
                    rules={[{ required: true, message: 'Please enter fee credit card' }]}
                  >
                    <Input size='large' autoComplete='off' placeholder='Please enter...' />
                  </Form.Item>
                  <Form.Item
                    name='tipFeeValue'
                    label='Fee Tip Credit Card'
                    rules={[{ required: true, message: 'Please enter fee tip credit card' }]}
                  >
                    <Input size='large' autoComplete='off' placeholder='Please enter...' />
                  </Form.Item>
                </Content>
                <BottomAction>
                  <Button ntype='SECONDARY' cardCashier htmlType='button' width='10rem' onClick={handleReset}>
                    Reset
                  </Button>
                  <Button ntype='PRIMARY' cardCashier htmlType='submit' width='10rem'>
                    Save
                  </Button>
                </BottomAction>
              </>
            )
          }
        </Form.Item>
      </Form>
    </Container>
  );
};

export default Fee;

const Container = styled.div`
  width: 100%;
  height: 100%;
  padding: 24px;
  box-sizing: border-box;
  background: #fff;
  .ant-switch {
    zoom: 1.5;
  }
`;

const Content = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-top: 24px;

  .ant-form-item {
    margin-bottom: 0;
  }

  .ant-form-item-label > label {
    font-size: 16px;
    font-weight: 500;
    font-family: Poppins;
  }

  .ant-input {
    height: 56px;
    font-size: 16px;
    font-weight: 500;
    font-family: Poppins;
  }

  .ant-select-selector,
  .ant-select-selection-item {
    font-size: 16px;
    font-weight: 500;
    font-family: Poppins;
  }
`;

const BottomAction = styled.div`
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
  gap: 24px;

  .CONTENT_2 {
    font-size: 20px;
    font-weight: 600;
    font-family: Poppins;
  }
`;
